import { atom, useSetAtom } from 'jotai';
import { useLayoutEffect } from 'react';

import type { LinkedContextItem } from '../../contracts/projects/linked-context';

/**
 * One-shot linked-context hand-off for callers that cannot reach the Work
 * composer's editor handle directly.
 *
 * Why this exists rather than the caller writing {@link setComposerLinkedItemAtom}
 * directly: Work home clears `composerLinkedItemsAtom` in a `useLayoutEffect` on
 * every entry without a `projectId`, so a stale chip from a back-navigation does
 * not resurrect the focused pre-chat surface. Every existing writer of the linked
 * item is rendered BY Work home, so none of them meets that reset. A sidebar row
 * that sets the item and then pushes the route does — the write lands before the
 * navigation and the layout effect wipes it before paint.
 *
 * Staging the intent in a separate atom lets the reset keep its meaning ("drop
 * whatever survived navigation") while an explicit hand-off still lands. Chip
 * intents are drained by the Work page AFTER the reset, in the same commit.
 * Token-preferring intents stay staged for the Composer, because only it owns
 * the live editor handle and knows whether the existing token gate is enabled.
 *
 * `openArtifact` asks whichever consumer handles the intent to open the item's
 * canvas too.
 * The nav card's Scribe action instead goes to the session route, so it does not
 * use this atom at all.
 *
 * Single-slot and one-shot: the drain clears it, so a second entry to Work home
 * does not re-attach a chip the user already dismissed.
 */
export interface PendingComposerLinkedContext {
  item: LinkedContextItem;
  /** Also open this item's canvas artifact when the hand-off is consumed. */
  openArtifact?: boolean;
  /** Prefer an inline token when the Composer's existing token gate allows it. */
  prefer?: 'token' | 'chip';
  /** The only surface allowed to claim a token-preferring hand-off. */
  destination?: 'work-home';
  /** Token intents are short-lived so an abandoned navigation cannot leak forward. */
  expiresAt?: number;
}

export const TOKEN_INTENT_TTL_MS = 30_000;

export function createPendingTokenIntent(
  item: LinkedContextItem,
  { openArtifact }: { openArtifact?: boolean } = {}
): PendingComposerLinkedContext {
  return {
    item,
    ...(openArtifact === undefined ? {} : { openArtifact }),
    prefer: 'token',
    destination: 'work-home',
    expiresAt: Date.now() + TOKEN_INTENT_TTL_MS,
  };
}

export const pendingComposerLinkedContextAtom = atom<PendingComposerLinkedContext | null>(
  null
);

/**
 * Presence signal owned by the only Composer allowed to consume token intents.
 * Its layout-effect registration runs before the Work page's parent drain, so
 * the drain observes the real committed consumer rather than reconstructing
 * its mount condition from route state. This is deliberately a boolean rather
 * than a refcount: at most one opted-in consumer may be mounted in production.
 */
export const pendingLinkedContextConsumerMountedAtom = atom(false);

/** Registers the sole pending-token consumer for the lifetime of its mount. */
export function useRegisterPendingLinkedContextConsumer(): void {
  const setConsumerMounted = useSetAtom(pendingLinkedContextConsumerMountedAtom);

  useLayoutEffect(() => {
    setConsumerMounted(true);
    return () => setConsumerMounted(false);
  }, [setConsumerMounted]);
}
